import { onMounted, onUnmounted, nextTick } from 'vue';

/**
 * Il comportamento da tastiera di un dialog che si apre **sopra** un altro:
 * fuoco iniziale dentro, trappola sul Tab, `Esc` per chiudere, e il fuoco
 * restituito all'elemento che l'ha aperto.
 *
 * Estratto perche' e' la seconda volta che serve — `AssociationConflictModal` e
 * `DuplicateReportLinesModal` — e sono le quaranta righe piu' facili da copiare
 * male: la trappola che non riporta il fuoco quando e' finito fuori, il
 * listener che non si stacca, l'`opener` che nessuno rimette a fuoco. Tre modi
 * diversi di lasciare chi naviga da tastiera dentro un modale da cui non si
 * esce, e nessuno dei tre si vede col mouse.
 *
 * Prende una `ref` all'elemento del dialog e la funzione che chiude, e non
 * conosce nulla del contenuto: titolo, icona e pulsanti restano di chi lo usa,
 * perche' quelli sono diversi ogni volta ed e' il markup che li descrive
 * meglio di una prop.
 *
 * Non si estende a `ImportRowErrorModal`, che ha la stessa forma: sta in un
 * altro flusso, e portarcelo dentro sarebbe un refactor laterale. Il candidato
 * resta, dichiarato.
 *
 * @param {import('vue').Ref<HTMLElement|null>} dialog l'elemento del dialog
 * @param {() => void} close cosa fare su `Esc`
 */
export const useModalFocusTrap = (dialog, close) =>
{
    // L'elemento che aveva il fuoco quando il dialog si e' aperto: alla
    // chiusura ci deve tornare, o chi naviga da tastiera riparte da capo.
    // Letto adesso, non all'`onMounted`: a quel punto il fuoco puo' essere
    // gia' stato spostato.
    const opener = typeof document !== 'undefined' ? document.activeElement : null;

    const focusables = () => Array.from(
        dialog.value?.querySelectorAll('button:not([disabled]), [href], input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])') ?? []
    );

    const onKeydown = (e) =>
    {
        if(e.key === 'Escape') return close();

        if(e.key !== 'Tab') return;

        const items = focusables();

        if(!items.length) return;

        const first = items[0];
        const last = items[items.length - 1];

        // Il fuoco e' finito fuori dal dialog: riportarlo dentro, altrimenti il
        // Tab successivo se ne va su quello che sta sotto.
        if(!dialog.value?.contains(document.activeElement))
        {
            e.preventDefault();

            return (e.shiftKey ? last : first).focus();
        }

        if(e.shiftKey && document.activeElement === first)
        {
            e.preventDefault();
            last.focus();
        }
        else if(!e.shiftKey && document.activeElement === last)
        {
            e.preventDefault();
            first.focus();
        }
    };

    onMounted( async () =>
    {
        document.addEventListener('keydown', onKeydown);

        await nextTick();
        focusables()[0]?.focus();
    });

    onUnmounted( () =>
    {
        document.removeEventListener('keydown', onKeydown);
        opener?.focus?.();
    });

    return { focusables };
};

export default useModalFocusTrap;
